import { Box, Typography } from '@mui/material';
import { Check, Copy, Minus, RefreshCw, SquareTerminal } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactElement } from 'react';

import { createAdminApi, type AdminHolder, type AdminPage, type AdminRole } from '@/admin/adminApi';
import { isForbidden, messageOf } from '@/admin/users/adminErrors';
import { formatInstant } from '@/admin/users/accountPresentation';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { useHttpClient } from '@/app/providers';
import {
  AccessDenied,
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  ErrorState,
  Icon,
  LoadingState,
  PageHeader,
  visuallyHidden,
  type DataTableColumn,
} from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import {
  CONSOLE_ONLY_NOTICE,
  GRANT_COMMAND,
  PERMISSIONS,
  REVOKE_HINT,
  ROLE_NAMES,
  formatGrantDate,
  roleCan,
  type PermissionRow,
} from './adminsModel';

/**
 * Administrateurs (`/admins`, BO-13) : qui detient un role d'exploitation.
 *
 * **Lecture seule, sans exception.** Aucun bouton n'octroie, ne retire ni ne
 * modifie un role : cela passe par la console (ADR 0032, point 4) et l'ecran le
 * dit — une mention sous le titre et la commande de production, copiable. Un
 * ecran qui cree un administrateur est un ecran qui s'en cree un tout seul.
 *
 * Trois regles :
 * 1. **Rien n'est invente.** Une date d'octroi absente de l'audit, ou une
 *    derniere connexion jamais ecrite, s'affiche « — ». La colonne « Double
 *    facteur » de la maquette n'existe pas (aucun modele, #524) : l'afficher
 *    « Absente » laisserait croire a une donnee.
 * 2. **Le 403 est une explication**, pas une panne : le compte est connecte, il
 *    lui manque un role (`support` suffit pour lire). Aucune autre requete.
 * 3. **Pas de rafraichissement automatique** (ADR 0035 §4) : « Actualiser » et
 *    la relecture au retour sur l'onglet.
 */

/** Page maximale que le serveur accepte : il y a moins de dix administrateurs. */
const PER_PAGE = 100;

interface LoadOutcome {
  /** Numero de la tentative qui a produit cette issue : l'attente en est deduite. */
  readonly attempt: number;
  /** Derniere page connue, gardee apres un echec de relecture. */
  readonly page: AdminPage<AdminHolder> | null;
  readonly error: string | null;
  readonly denied: boolean;
}

const useAdminHolders = () => {
  const http = useHttpClient();
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<LoadOutcome | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const page = await createAdminApi(http).listAdmins(1, PER_PAGE);

        if (!cancelled) {
          setOutcome({ attempt, page, error: null, denied: false });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setOutcome((previous) => ({
            attempt,
            page: previous?.page ?? null,
            error: isForbidden(error) ? null : messageOf(error),
            denied: isForbidden(error),
          }));
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [http, attempt]);

  const reload = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(reload);

  const settled = outcome?.attempt === attempt ? outcome : null;

  return {
    busy: settled === null,
    page: outcome?.page ?? null,
    error: settled?.error ?? null,
    denied: settled?.denied ?? false,
    reload,
  };
};

/**
 * Commande d'octroi de production, avec « Copier ».
 *
 * La copie est **facultative** : `navigator.clipboard` n'existe pas partout
 * (page non securisee, navigateur restreint) et son echec ne doit rien casser —
 * la commande reste affichee et selectionnable. « Copié » est annonce poliment.
 */
const ConsoleCommand = (): ReactElement => {
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    try {
      void navigator.clipboard
        .writeText(GRANT_COMMAND)
        .then(() => {
          setCopied(true);
        })
        .catch(() => {
          setCopied(false);
        });
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      component="section"
      aria-label="Commande d’octroi d’un rôle"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        px: 2,
        py: 1.75,
        borderRadius: '14px',
        border: 1,
        borderColor: 'tokens.border',
        bgcolor: 'tokens.surfaceMuted',
      }}
    >
      <Box sx={{ color: 'tokens.textMuted', display: 'inline-flex' }}>
        <Icon icon={SquareTerminal} size={20} />
      </Box>
      <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
        <Typography
          component="code"
          sx={{ display: 'block', fontFamily: fontStacks.mono, fontSize: 14, fontWeight: 500, overflowWrap: 'anywhere' }}
        >
          {GRANT_COMMAND}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: 'tokens.textMuted' }}>
          À jouer sur le serveur de production, dans le dépôt cloné. {REVOKE_HINT}
        </Typography>
      </Box>
      <Button size="S" icon={Copy} onClick={copy}>
        {copied ? 'Copié' : 'Copier'}
      </Button>
      <Box component="span" role="status" sx={visuallyHidden}>
        {copied ? 'Commande copiée dans le presse-papiers.' : ''}
      </Box>
    </Box>
  );
};

const holderColumns: readonly DataTableColumn<AdminHolder>[] = [
  {
    key: 'account',
    header: 'Compte',
    primary: true,
    render: (holder) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Avatar name={holder.email} tone={holder.adminRole === 'admin' ? 'pink' : 'blue'} />
        <Box component="span" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
          {holder.email}
        </Box>
      </Box>
    ),
  },
  {
    key: 'role',
    header: 'Rôle',
    render: (holder) => <Badge label={ROLE_NAMES[holder.adminRole]} tone={holder.adminRole === 'admin' ? 'brand' : 'info'} />,
  },
  {
    key: 'lastLogin',
    header: 'Dernière connexion',
    render: (holder) => (holder.lastLoginAt === null ? null : formatInstant(holder.lastLoginAt, '')),
  },
  { key: 'since', header: 'Depuis', render: (holder) => formatGrantDate(holder.roleGrantedAt) },
];

/** Coche ou tiret : le texte masque porte le sens, la forme ne le porte pas seule. */
const AllowedMark = ({ allowed }: { allowed: boolean }): ReactElement => (
  <Box
    component="span"
    sx={{ display: 'inline-flex', color: allowed ? 'tokens.successText' : 'tokens.textMuted' }}
  >
    <Icon icon={allowed ? Check : Minus} size={18} />
    <Box component="span" sx={visuallyHidden}>
      {allowed ? 'Oui' : 'Non'}
    </Box>
  </Box>
);

const matrixColumn = (role: AdminRole): DataTableColumn<PermissionRow> => ({
  key: role,
  header: ROLE_NAMES[role],
  align: 'right',
  render: (permission) => <AllowedMark allowed={roleCan(role, permission)} />,
});

const matrixColumns: readonly DataTableColumn<PermissionRow>[] = [
  { key: 'permission', header: 'Permission', primary: true, render: (permission) => permission.label },
  matrixColumn('support'),
  matrixColumn('admin'),
];

export const AdminsScreen = (): ReactElement => {
  const { busy, page, error, denied, reload } = useAdminHolders();
  const items = page?.items ?? [];
  const totalKnown = page !== null && page.total >= 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        eyebrow="Réglages"
        title="Administrateurs"
        subtitle={CONSOLE_ONLY_NOTICE}
        actions={
          <Button icon={RefreshCw} onClick={reload} disabled={busy}>
            {busy ? 'Actualisation…' : 'Actualiser'}
          </Button>
        }
      />

      {denied ? (
        <AccessDenied title="Votre compte n’a pas accès à cet écran">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration. Un rôle s’accorde en console sur le
          serveur, jamais depuis cet écran.
        </AccessDenied>
      ) : (
        <>
          <ConsoleCommand />

          {error === null ? null : <ErrorState message={error} onRetry={reload} />}

          <Card
            title="Détenteurs d’un rôle"
            subtitle={totalKnown ? `${String(page.total)} compte${page.total > 1 ? 's' : ''} actif${page.total > 1 ? 's' : ''}` : undefined}
          >
            {page === null && busy ? <LoadingState label="Chargement des administrateurs…" /> : null}
            {page === null ? null : (
              <DataTable
                label="Administrateurs"
                columns={holderColumns}
                rows={items}
                getRowId={(holder) => holder.userId}
                emptyMessage="Aucun compte actif ne détient de rôle d’exploitation."
              />
            )}
            {totalKnown && page.total > items.length ? (
              <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                Seuls les {String(items.length)} premiers comptes sont affichés sur {String(page.total)}.
              </Typography>
            ) : null}
          </Card>

          <Card title="Ce que chaque rôle peut faire" subtitle="Relevé statique des droits que l’API applique.">
            <DataTable
              label="Droits par rôle"
              columns={matrixColumns}
              rows={PERMISSIONS}
              getRowId={(permission) => permission.id}
            />
          </Card>
        </>
      )}
    </Box>
  );
};
